import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import { adminApiService } from '../../services/adminApiService';
import CoinPositionsDrawer from '../market/CoinPositionsDrawer';
import ActivityBotCreateDrawer from '../crypto/ActivityBotCreateDrawer';
import { CoinActivity } from '../../services/publicMarketService';
import { useAppSelector } from '../../store/hooks';
import { adminCanOpenMarketSignalPositions } from '../../utils/accessControl';

type Instrument = {
  id: string;
  symbol: string;
  name: string;
  category: string;
  price: number;
  change: number;
  changePercent: number;
  high?: number;
  low?: number;
  currency?: string;
  source?: string;
  sparkline?: number[];
  bias?: string;
  note?: string;
  kucoinTradeable?: boolean;
  kucoinFuturesSymbol?: string;
  kucoinSpotSymbols?: string[];
  kucoinTradeBase?: string;
  kucoinNote?: string;
};

type NewsEvent = {
  id: string;
  headline: string;
  source?: string;
  url?: string;
  publishedEpochSec?: number;
  scenario?: string;
  summary?: string;
  expectedImpacts?: Array<{ asset: string; direction: string; reason: string }>;
  confidence?: number;
  liveHeadline?: boolean;
};

type MacroEngine = {
  mode?: string;
  baselineCode?: string;
  baselineConfidence?: number;
  dampenFactor?: number;
  effectiveConfidence?: number;
  biasFrozen?: boolean;
  freezeReason?: string;
  freezeUntilEpochSec?: number;
  narrativeOverrideActive?: boolean;
  narrativeOverrideCode?: string;
  narratives?: Array<{
    id?: string;
    scenario?: string;
    representativeHeadline?: string;
    memberCount?: number;
    decayedWeight?: number;
    decayFactor?: number;
    ageHours?: number;
  }>;
  liquidityBias?: string;
  liquidityScore?: number;
  liquidityNote?: string;
  correlationBreaks?: string[];
  layerNotes?: string[];
};

type MacroRegime = {
  code?: string;
  label?: string;
  summary?: string;
  confidence?: number;
  drivers?: string[];
  scopeNote?: string;
  engine?: MacroEngine;
  btcBias?: {
    bias?: string;
    longBlockRecommended?: boolean;
    shortBlockRecommended?: boolean;
    reason?: string;
    sourceRegimeCode?: string;
    sourceConfidence?: number;
    scopeNote?: string;
  };
  commodityBias?: {
    bias?: string;
    skipLongSymbols?: string[];
    skipShortSymbols?: string[];
    reason?: string;
    sourceRegimeCode?: string;
    sourceConfidence?: number;
    scopeNote?: string;
  };
};

type CommoditySuggestion = {
  instrumentId?: string;
  symbol?: string;
  name?: string;
  side?: string;
  reason?: string;
  confidence?: number;
  kucoinTradeBase?: string;
  kucoinFuturesSymbol?: string;
  kucoinTradeable?: boolean;
};

type StockSuggestion = {
  instrumentId?: string;
  symbol?: string;
  name?: string;
  side?: string;
  reason?: string;
  confidence?: number;
};

type Board = {
  asOfEpochSec?: number;
  disclaimer?: string;
  instruments?: Instrument[];
  byCategory?: Record<string, Instrument[]>;
  triggerEvents?: NewsEvent[];
  correlationMatrix?: Array<{ symbol: string; values: Record<string, number> }>;
  correlationSymbols?: string[];
  playbooks?: Array<{ id: string; trigger: string; typicalMove: string; tradingNote: string }>;
  macroRegime?: MacroRegime;
  commoditySuggestions?: CommoditySuggestion[];
  stockSuggestions?: StockSuggestion[];
};

const CATEGORY_ORDER = ['COMMODITY', 'FOREX', 'CRYPTO', 'STOCK'];

const MACRO_REGIME_COLORS: Record<string, string> = {
  GEOPOLITICAL_ENERGY: '#ea580c',
  OIL_SUPPLY: '#c2410c',
  RISK_OFF: '#6366f1',
  RISK_ON: '#059669',
  MACRO_RATES: '#0284c7',
  MIXED: '#64748b',
  UNKNOWN: '#9ca3af',
};

type ReadLevel = 'CLEAR' | 'MIXED' | 'STAND_DOWN';

type SuggestionAlignment = {
  level: 'BOARD_ALIGNED' | 'PLAYBOOK_ONLY' | 'CONFLICTS';
  label: string;
  chipColor: 'success' | 'warning' | 'error' | 'default';
  tip: string;
};

type ReadGuidance = {
  level: ReadLevel;
  title: string;
  message: string;
  severity: 'success' | 'warning' | 'error' | 'info';
};

function findBoardInstrument(
  instruments: Instrument[] | undefined,
  instrumentId?: string,
  symbol?: string
): Instrument | undefined {
  if (!instruments?.length) return undefined;
  if (instrumentId) {
    const byId = instruments.find((i) => i.id?.toLowerCase() === instrumentId.toLowerCase());
    if (byId) return byId;
  }
  if (symbol) {
    const sym = symbol.toUpperCase();
    return instruments.find((i) => (i.symbol || '').toUpperCase() === sym);
  }
  return undefined;
}

/** How well a suggestion agrees with the live price board + engine state. */
function getSuggestionAlignment(
  side: string | undefined,
  inst: Instrument | undefined,
  regime: MacroRegime | undefined
): SuggestionAlignment {
  const s = (side || '').toUpperCase();
  const frozen = !!regime?.engine?.biasFrozen;
  const conf = regime?.confidence ?? 0;

  if (!inst) {
    return {
      level: 'PLAYBOOK_ONLY',
      label: 'Playbook only',
      chipColor: 'warning',
      tip: 'No matching price-board row to confirm this idea.',
    };
  }

  const bias = (inst.bias || '').toUpperCase();
  const pct = Number(inst.changePercent) || 0;
  const longOk = s === 'LONG' && (bias === 'BULLISH' || pct >= 0.25);
  const shortOk = s === 'SHORT' && (bias === 'BEARISH' || pct <= -0.25);
  const longConflict = s === 'LONG' && (bias === 'BEARISH' || pct <= -0.5);
  const shortConflict = s === 'SHORT' && (bias === 'BULLISH' || pct >= 0.5);

  if (longConflict || shortConflict) {
    return {
      level: 'CONFLICTS',
      label: 'Conflicts with tape',
      chipColor: 'error',
      tip: `Board shows ${bias || `${pct.toFixed(2)}%`} — opposite to suggested ${s}. Prefer skipping.`,
    };
  }

  if (longOk || shortOk) {
    const tapeTip = `Board ${bias || 'FLAT'} ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}% agrees with ${s}.`;
    if (frozen) {
      return {
        level: 'PLAYBOOK_ONLY',
        label: 'Board aligned · bias frozen',
        chipColor: 'warning',
        tip: `${tapeTip} Macro bias is frozen — research only; opted-in bots still skip new shorts without clear BEARISH.`,
      };
    }
    if (conf < 0.4) {
      return {
        level: 'PLAYBOOK_ONLY',
        label: 'Board aligned · low confidence',
        chipColor: 'warning',
        tip: `${tapeTip} Engine confidence is low — smaller size or wait.`,
      };
    }
    if (conf < 0.55) {
      return {
        level: 'PLAYBOOK_ONLY',
        label: 'Board aligned · caution',
        chipColor: 'warning',
        tip: `${tapeTip} Macro confidence is below the usual 55% seatbelt threshold.`,
      };
    }
    return {
      level: 'BOARD_ALIGNED',
      label: 'Board aligned',
      chipColor: 'success',
      tip: tapeTip,
    };
  }

  return {
    level: 'PLAYBOOK_ONLY',
    label: 'Playbook only',
    chipColor: 'warning',
    tip: 'Suggested by regime playbook; board bias is flat or weak.',
  };
}

function buildReadGuidance(board: Board | null | undefined): ReadGuidance | null {
  if (!board?.macroRegime) return null;
  const regime = board.macroRegime;
  const eng = regime.engine;
  const conf = regime.confidence ?? 0;
  const frozen = !!eng?.biasFrozen;
  const corrN = eng?.correlationBreaks?.length ?? 0;
  const dampen = eng?.dampenFactor ?? 1;
  const allInstr = board.instruments || [];

  const scored = [
    ...(board.commoditySuggestions || []).map((s) =>
      getSuggestionAlignment(s.side, findBoardInstrument(allInstr, s.instrumentId, s.symbol), regime)
    ),
    ...(board.stockSuggestions || []).map((s) =>
      getSuggestionAlignment(s.side, findBoardInstrument(allInstr, s.instrumentId, s.symbol), regime)
    ),
  ];
  const aligned = scored.filter((a) => a.level === 'BOARD_ALIGNED').length;
  const caution = scored.filter((a) => a.level === 'PLAYBOOK_ONLY').length;
  const conflicts = scored.filter((a) => a.level === 'CONFLICTS').length;
  const commodityN = board.commoditySuggestions?.length ?? 0;
  const stockN = board.stockSuggestions?.length ?? 0;

  if (frozen || conf < 0.4) {
    const bits: string[] = [];
    if (frozen) bits.push('macro bias is frozen (long seatbelt off; opted-in bots still skip shorts)');
    if (conf < 0.4) bits.push(`confidence is only ${(conf * 100).toFixed(0)}%`);
    if (commodityN > 0) bits.push('prefer commodity ideas that still show “Board aligned”');
    if (stockN > 0 && caution + conflicts >= aligned) bits.push('equity suggestions are weak or conflicted');
    return {
      level: 'STAND_DOWN',
      title: 'Stand down / research only',
      message:
        bits.length > 0
          ? `${bits.join('; ')}. Suggestions are not orders — confirm on the price board before opening.`
          : 'Engine confidence is too low to lean on macro seatbelts. Use the price board; treat suggestions as research only.',
      severity: 'error',
    };
  }

  if (corrN > 0 || dampen < 0.9 || caution > 0 || conflicts > 0 || conf < 0.55) {
    const bits: string[] = [];
    if (aligned > 0) bits.push(`${aligned} idea(s) strongly board-aligned`);
    if (caution > 0) bits.push(`${caution} idea(s) need caution`);
    if (conflicts > 0) bits.push(`${conflicts} conflict with the tape — skip those`);
    if (corrN > 0) bits.push(`${corrN} correlation break(s)`);
    if (dampen < 0.9) bits.push(`confidence dampened ×${dampen.toFixed(2)}`);
    return {
      level: 'MIXED',
      title: 'Mixed tape',
      message:
        (bits.length > 0 ? `${bits.join('; ')}. ` : '')
        + 'Prefer green “Board aligned” cards; skip red conflicts. Banner mood ≠ auto-trade.',
      severity: 'warning',
    };
  }

  return {
    level: 'CLEAR',
    title: 'Clearer read',
    message:
      aligned > 0
        ? `Macro and board mostly agree. Prefer the ${aligned} “Board aligned” suggestion(s); still research only — not auto-trade.`
        : 'Macro confidence is usable and seatbelts are active if opted in. Confirm each idea on the price board.',
    severity: 'success',
  };
}

const fmtPrice = (n?: number, currency = 'USD') => {
  if (n == null || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const digits = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency === 'JPY' ? 'JPY' : 'USD',
      maximumFractionDigits: digits,
    }).format(n);
  } catch {
    return n.toFixed(digits);
  }
};

const pctColor = (p?: number) => {
  if (p == null) return '#64748b';
  if (p > 0.05) return '#059669';
  if (p < -0.05) return '#dc2626';
  return '#64748b';
};

const kucoinFuturesUrl = (symbol: string) =>
  `https://www.kucoin.com/trade/futures/${encodeURIComponent(symbol)}`;

const kucoinSpotUrl = (symbol: string) =>
  `https://www.kucoin.com/trade/${encodeURIComponent(symbol)}`;

/** Map Market Intelligence row → drawer coin using KuCoin futures base (not Yahoo board symbol). */
const toTradeCoin = (row: Instrument): CoinActivity => {
  let base =
    (row.kucoinTradeBase && row.kucoinTradeBase.trim()) ||
    (row.kucoinFuturesSymbol
      ? row.kucoinFuturesSymbol.replace(/USDTM$/i, '').toUpperCase()
      : row.symbol);
  if (base.toUpperCase() === 'XBT') base = 'BTC';
  const pct = row.changePercent ?? 0;
  let assetTypeLabel: string | undefined;
  if (row.category === 'STOCK') assetTypeLabel = 'NASDAQ Stock Perpetual';
  else if (row.category === 'COMMODITY') assetTypeLabel = 'Commodity Perpetual';
  else if (row.category === 'CRYPTO') assetTypeLabel = 'Crypto Perpetual';
  return {
    symbol: base.toUpperCase(),
    displayName: row.name,
    futuresSymbol: row.kucoinFuturesSymbol || null,
    price: row.price,
    change24h: pct,
    volume24h: 0,
    active: pct >= 0,
    assetTypeLabel,
    sectorLabel: row.category || null,
  };
};

const KucoinTradeChips: React.FC<{
  instrument: Instrument;
  dense?: boolean;
  canTrade?: boolean;
  onTrade?: (instrument: Instrument) => void;
  /** Opens Stock Portfolio with this board symbol for Trading 212 equity trading. */
  canTradeT212?: boolean;
  onTradeT212?: (instrument: Instrument) => void;
}> = ({ instrument, dense, canTrade, onTrade, canTradeT212, onTradeT212 }) => {
  const isStock = instrument.category === 'STOCK';
  const showT212 = isStock && !!canTradeT212 && !!onTradeT212;
  if (!instrument.kucoinTradeable && !showT212) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }
  const tip =
    instrument.kucoinNote ||
    'KuCoin crypto product — not the same as Yahoo/CNBC exchange futures.';
  return (
    <Stack
      direction="row"
      gap={0.5}
      flexWrap="wrap"
      useFlexGap
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {instrument.kucoinTradeable && (
        <Tooltip title={tip} arrow>
          <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
            {canTrade && onTrade && (
              <Chip
                clickable
                size="small"
                color="secondary"
                variant="filled"
                label="Trade"
                onClick={() => onTrade(instrument)}
                sx={{ fontWeight: 700, height: dense ? 22 : 24 }}
              />
            )}
            {instrument.kucoinFuturesSymbol && (
              <Chip
                component="a"
                href={kucoinFuturesUrl(instrument.kucoinFuturesSymbol)}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                size="small"
                color="primary"
                variant={dense ? 'outlined' : 'filled'}
                label={`KuCoin ${instrument.kucoinFuturesSymbol}`}
                sx={{ fontWeight: 600, height: dense ? 22 : 24 }}
              />
            )}
            {(instrument.kucoinSpotSymbols || []).slice(0, 2).map((pair) => (
              <Chip
                key={pair}
                component="a"
                href={kucoinSpotUrl(pair)}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                size="small"
                variant="outlined"
                label={`Spot ${pair}`}
                sx={{ height: dense ? 22 : 24 }}
              />
            ))}
          </Stack>
        </Tooltip>
      )}
      {showT212 && (
        <Tooltip
          title="Open this stock on your Trading 212 account (Stock Portfolio — real equities, not KuCoin futures)"
          arrow
        >
          <Chip
            clickable
            size="small"
            variant="filled"
            label="T212 Trade"
            onClick={() => onTradeT212!(instrument)}
            sx={{
              fontWeight: 700,
              height: dense ? 22 : 24,
              background: '#0b6efd',
              color: '#fff',
              '&:hover': { background: '#0958d9' },
            }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

const AdminMarkets: React.FC = () => {
  const navigate = useNavigate();
  const adminUser = useAppSelector((state) => state.auth.adminUser);
  const adminPermissions = adminUser?.permissions || [];
  const canOpenPositions = useMemo(
    () => adminCanOpenMarketSignalPositions(adminUser ?? undefined),
    [adminUser]
  );
  const canTradeT212 = adminPermissions.includes('VIEW_STOCK_PORTFOLIO');

  const openTrading212 = useCallback(
    (instrument: Instrument) => {
      if (instrument.category !== 'STOCK' || !instrument.symbol) return;
      // Hand off via sessionStorage — putting t212Symbol in the URL fights the
      // admin dashboard tab-sync (double refresh) and often clears before the drawer opens.
      try {
        sessionStorage.setItem('stockPortfolio.pendingT212Symbol', instrument.symbol);
      } catch {
        // sessionStorage unavailable — Stock Portfolio still opens; user can search.
      }
      navigate('/admin/dashboard?tab=stockPortfolio');
    },
    [navigate],
  );

  const [tab, setTab] = useState(0);
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [selectedId, setSelectedId] = useState<string>('crude_oil');
  const [range, setRange] = useState('1mo');
  const [historyPoints, setHistoryPoints] = useState<Array<{ t: number; price: number; label: string }>>([]);
  const [historySource, setHistorySource] = useState<string>('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCoin, setDrawerCoin] = useState<CoinActivity | null>(null);
  const [activityBotDrawerOpen, setActivityBotDrawerOpen] = useState(false);
  const [activityBotSavedMsg, setActivityBotSavedMsg] = useState<string | null>(null);

  const openTradeDrawer = useCallback(
    (instrument: Instrument) => {
      if (!instrument.kucoinTradeable) return;
      setDrawerCoin(toTradeCoin(instrument));
      setDrawerOpen(true);
    },
    []
  );

  const openSuggestionTrade = useCallback(
    (suggestion: CommoditySuggestion) => {
      const list = board?.instruments || [];
      const row =
        list.find((i) => i.id === suggestion.instrumentId) ||
        list.find(
          (i) =>
            suggestion.kucoinTradeBase &&
            i.kucoinTradeBase?.toUpperCase() === suggestion.kucoinTradeBase.toUpperCase()
        );
      if (!row || !row.kucoinTradeable) return;
      const coin = toTradeCoin(row);
      const wantShort = (suggestion.side || '').toUpperCase() === 'SHORT';
      // Drawer defaults side from coin.active (HOT→LONG, COOL→SHORT)
      setDrawerCoin({ ...coin, active: !wantShort });
      setDrawerOpen(true);
    },
    [board?.instruments]
  );

  const openStockSuggestionT212 = useCallback(
    (suggestion: StockSuggestion) => {
      const symbol = suggestion.symbol?.trim();
      if (!symbol) return;
      const list = board?.instruments || [];
      const row = list.find((i) => i.id === suggestion.instrumentId)
        || list.find((i) => i.symbol?.toUpperCase() === symbol.toUpperCase());
      if (row) {
        openTrading212(row);
        return;
      }
      try {
        sessionStorage.setItem('stockPortfolio.pendingT212Symbol', symbol);
      } catch {
        // sessionStorage unavailable
      }
      navigate('/admin/dashboard?tab=stockPortfolio');
    },
    [board?.instruments, navigate, openTrading212]
  );

  /** SHORT (and optional LONG) stock ideas execute on KuCoin stock-index perps. */
  const openStockSuggestionKuCoin = useCallback(
    (suggestion: StockSuggestion) => {
      const list = board?.instruments || [];
      const symbol = suggestion.symbol?.trim();
      const row =
        list.find((i) => i.id === suggestion.instrumentId) ||
        (symbol
          ? list.find((i) => i.symbol?.toUpperCase() === symbol.toUpperCase())
          : undefined);
      if (!row || !row.kucoinTradeable) return;
      const coin = toTradeCoin(row);
      const wantShort = (suggestion.side || '').toUpperCase() === 'SHORT';
      setDrawerCoin({ ...coin, active: !wantShort });
      setDrawerOpen(true);
    },
    [board?.instruments]
  );

  const resolveStockSuggestionRow = useCallback(
    (suggestion: StockSuggestion): Instrument | undefined => {
      const list = board?.instruments || [];
      const symbol = suggestion.symbol?.trim();
      return (
        list.find((i) => i.id === suggestion.instrumentId) ||
        (symbol
          ? list.find((i) => i.symbol?.toUpperCase() === symbol.toUpperCase())
          : undefined)
      );
    },
    [board?.instruments]
  );

  const handleCloseTradeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleViewStrategyFromDrawer = useCallback(
    (strategyId: number, symbol: string) => {
      setDrawerOpen(false);
      navigate(
        `/admin/dashboard?tab=tradingBots&strategyId=${strategyId}&symbol=${encodeURIComponent(symbol)}`
      );
    },
    [navigate]
  );

  const loadBoard = useCallback(async () => {
    try {
      setError(null);
      const res = await adminApiService.getMarketsBoard();
      if (res.success && res.data) {
        setBoard(res.data as Board);
        const instruments = (res.data as Board).instruments || [];
        if (instruments.length && !instruments.find((i) => i.id === selectedId)) {
          setSelectedId(instruments[0].id);
        }
      } else {
        setError(res.error || 'Failed to load markets board');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load markets board');
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const loadHistory = useCallback(async (id: string, rng: string) => {
    setHistoryLoading(true);
    try {
      const res = await adminApiService.getMarketsHistory(id, rng);
      if (res.success && res.data?.points) {
        const pts = (res.data.points as Array<{ t: number; price: number }>).map((p) => ({
          t: p.t,
          price: p.price,
          label: new Date(p.t * 1000).toLocaleString(),
        }));
        setHistoryPoints(pts);
        setHistorySource(String(res.data.source || ''));
      } else {
        setHistoryPoints([]);
        setHistorySource('');
      }
    } catch {
      setHistoryPoints([]);
      setHistorySource('');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      loadBoard();
    }, 45000);
    return () => window.clearInterval(id);
  }, [autoRefresh, loadBoard]);

  useEffect(() => {
    if (selectedId) {
      loadHistory(selectedId, range);
    }
  }, [selectedId, range, loadHistory]);

  const instruments = useMemo(() => {
    const list = board?.instruments || [];
    if (categoryFilter === 'ALL') return list;
    return list.filter((i) => i.category === categoryFilter);
  }, [board, categoryFilter]);

  const selected = useMemo(
    () => (board?.instruments || []).find((i) => i.id === selectedId),
    [board, selectedId]
  );

  const readGuidance = useMemo(() => buildReadGuidance(board), [board]);

  const corrSymbols = board?.correlationSymbols || [];

  const heatColor = (v: number) => {
    if (v >= 0.7) return 'rgba(5,150,105,0.35)';
    if (v >= 0.3) return 'rgba(5,150,105,0.15)';
    if (v <= -0.7) return 'rgba(220,38,38,0.35)';
    if (v <= -0.3) return 'rgba(220,38,38,0.15)';
    return 'transparent';
  };

  if (loading && !board) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          minHeight: { xs: '50vh', md: 320 },
          py: 4,
          boxSizing: 'border-box',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0.5, md: 2 }, maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        gap={1.5}
        mb={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
            Market Intelligence
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Live commodities, FX, crypto &amp; equities — with news trigger playbooks and correlations
          </Typography>
        </Box>
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            width: { xs: '100%', md: 'auto' },
            '& .MuiButton-root': {
              flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
              minWidth: { xs: 0, sm: 'auto' },
            },
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={!canOpenPositions}
            onClick={() => {
              setActivityBotSavedMsg(null);
              setActivityBotDrawerOpen(true);
            }}
          >
            Create commodities activity bot
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/admin/dashboard?tab=cryptoPortfolio')}
          >
            Manage bots
          </Button>
          <Button variant="outlined" size="small" onClick={() => loadBoard()}>
            Refresh
          </Button>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setAutoRefresh((v) => !v)}
          >
            Auto {autoRefresh ? 'ON' : 'OFF'} (45s)
          </Button>
          {board?.asOfEpochSec && (
            <Chip
              size="small"
              label={`As of ${new Date(board.asOfEpochSec * 1000).toLocaleTimeString()}`}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
            />
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {activityBotSavedMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActivityBotSavedMsg(null)}>
          {activityBotSavedMsg}{' '}
          <Button
            size="small"
            color="inherit"
            onClick={() => navigate('/admin/dashboard?tab=cryptoPortfolio')}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Open Crypto Portfolio
          </Button>
        </Alert>
      )}
      {!canOpenPositions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Creating activity bots requires open-position / trading-bot manage permission. You can still view Market Intelligence.
        </Alert>
      )}
      {board?.macroRegime && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            p: 1.5,
            borderColor: MACRO_REGIME_COLORS[board.macroRegime.code || ''] || MACRO_REGIME_COLORS.MIXED,
            bgcolor: `${MACRO_REGIME_COLORS[board.macroRegime.code || ''] || MACRO_REGIME_COLORS.MIXED}12`,
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: MACRO_REGIME_COLORS[board.macroRegime.code || ''] || MACRO_REGIME_COLORS.MIXED,
                }}
              />
              <Typography fontWeight={800} fontSize={14}>
                Macro regime:{' '}
                <Box component="span" sx={{ color: MACRO_REGIME_COLORS[board.macroRegime.code || ''] || MACRO_REGIME_COLORS.MIXED }}>
                  {board.macroRegime.label || board.macroRegime.code}
                </Box>
              </Typography>
              {board.macroRegime.code && (
                <Chip size="small" label={board.macroRegime.code} variant="outlined" />
              )}
              {board.macroRegime.confidence != null && (
                <Chip
                  size="small"
                  label={`Confidence ${(board.macroRegime.confidence * 100).toFixed(0)}%`}
                  variant="outlined"
                />
              )}
              {board.macroRegime.btcBias?.bias && board.macroRegime.btcBias.bias !== 'NONE' && (
                <Tooltip title={board.macroRegime.btcBias.reason || board.macroRegime.btcBias.scopeNote || ''}>
                  <Chip
                    size="small"
                    color={
                      board.macroRegime.btcBias.bias === 'BEARISH'
                        ? 'error'
                        : board.macroRegime.btcBias.bias === 'BULLISH'
                          ? 'success'
                          : 'default'
                    }
                    variant={
                      board.macroRegime.btcBias.longBlockRecommended
                      || board.macroRegime.btcBias.shortBlockRecommended
                        ? 'filled'
                        : 'outlined'
                    }
                    label={
                      board.macroRegime.btcBias.longBlockRecommended
                        ? `BTC: ${board.macroRegime.btcBias.bias} (long-block if opted in)`
                        : board.macroRegime.btcBias.shortBlockRecommended
                          ? `BTC: ${board.macroRegime.btcBias.bias} (short-block if opted in)`
                          : `BTC bias: ${board.macroRegime.btcBias.bias}`
                    }
                    sx={{
                      maxWidth: { xs: '100%', sm: 'none' },
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: { xs: 'normal', sm: 'nowrap' },
                        display: 'block',
                        py: { xs: 0.5, sm: 0 },
                      },
                    }}
                  />
                </Tooltip>
              )}
              {board.macroRegime.commodityBias?.bias && board.macroRegime.commodityBias.bias !== 'NONE' && (
                <Tooltip title={board.macroRegime.commodityBias.reason || board.macroRegime.commodityBias.scopeNote || ''}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`Commodity: ${board.macroRegime.commodityBias.bias}`}
                  />
                </Tooltip>
              )}
              {board.macroRegime.engine?.mode && (
                <Chip size="small" variant="outlined" label={board.macroRegime.engine.mode} />
              )}
              {board.macroRegime.engine?.biasFrozen && (
                <Tooltip title={board.macroRegime.engine.freezeReason || 'Macro bias frozen'}>
                  <Chip size="small" color="error" variant="filled" label="Bias frozen" />
                </Tooltip>
              )}
              {board.macroRegime.engine?.narrativeOverrideActive && (
                <Tooltip title={`Headline cluster leading: ${board.macroRegime.engine.narrativeOverrideCode || ''}`}>
                  <Chip
                    size="small"
                    color="secondary"
                    variant="outlined"
                    label={`Narrative override: ${board.macroRegime.engine.narrativeOverrideCode || 'ON'}`}
                  />
                </Tooltip>
              )}
              {board.macroRegime.engine?.liquidityBias && board.macroRegime.engine.liquidityBias !== 'UNKNOWN' && (
                <Tooltip title={board.macroRegime.engine.liquidityNote || ''}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Liquidity: ${board.macroRegime.engine.liquidityBias}`}
                  />
                </Tooltip>
              )}
              {(board.macroRegime.engine?.correlationBreaks || []).length > 0 && (
                <Tooltip title={(board.macroRegime.engine?.correlationBreaks || []).join(' · ')}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="filled"
                    label={`Corr breaks: ${board.macroRegime.engine!.correlationBreaks!.length}`}
                  />
                </Tooltip>
              )}
              {board.macroRegime.engine?.dampenFactor != null
                && board.macroRegime.engine.dampenFactor < 0.99 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Dampen ×${board.macroRegime.engine.dampenFactor.toFixed(2)}`}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ maxWidth: { xs: '100%', md: 520 } }}
            >
              Dynamic engine (narrative / decay / circuit / liquidity / corr). Research only — bot gates only if opted in.
            </Typography>
          </Stack>
          {board.macroRegime.summary && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {board.macroRegime.summary}
            </Typography>
          )}
          {(board.macroRegime.engine?.narratives || []).length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Top narrative:{' '}
              {board.macroRegime.engine!.narratives![0].scenario}
              {board.macroRegime.engine!.narratives![0].memberCount != null
                ? ` (${board.macroRegime.engine!.narratives![0].memberCount} headlines → 1 weight)`
                : ''}
              {board.macroRegime.engine!.narratives![0].decayedWeight != null
                ? ` · weight ${board.macroRegime.engine!.narratives![0].decayedWeight.toFixed(2)}`
                : ''}
              {board.macroRegime.engine!.narratives![0].representativeHeadline
                ? ` — ${board.macroRegime.engine!.narratives![0].representativeHeadline}`
                : ''}
            </Typography>
          )}
          {(board.macroRegime.drivers || []).length > 0 && (
            <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap mt={1}>
              {board.macroRegime.drivers!.map((d) => (
                <Chip key={d} size="small" label={d} />
              ))}
            </Stack>
          )}
        </Paper>
      )}
      {readGuidance && (
        <Alert
          severity={readGuidance.severity}
          sx={{ mb: 2 }}
          icon={false}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'flex-start' }}>
            <Chip
              size="small"
              color={
                readGuidance.level === 'CLEAR'
                  ? 'success'
                  : readGuidance.level === 'MIXED'
                    ? 'warning'
                    : 'error'
              }
              label={readGuidance.title}
              sx={{ fontWeight: 800, flexShrink: 0 }}
            />
            <Typography variant="body2" sx={{ pt: { sm: 0.25 } }}>
              {readGuidance.message}
            </Typography>
          </Stack>
        </Alert>
      )}
      {(board?.commoditySuggestions || []).length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} mb={1}>
            <Typography fontWeight={800} fontSize={14}>
              Suggested commodities to open
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Research only — green = board agrees; amber = caution; red = skip. Not auto-trade.
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
            {board!.commoditySuggestions!.map((s) => {
              const long = (s.side || '').toUpperCase() === 'LONG';
              const alignment = getSuggestionAlignment(
                s.side,
                findBoardInstrument(board?.instruments, s.instrumentId, s.symbol),
                board?.macroRegime
              );
              return (
                <Paper
                  key={`${s.instrumentId}-${s.side}`}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    minWidth: { xs: 0, sm: 200 },
                    flex: { xs: '1 1 100%', sm: '1 1 200px' },
                    maxWidth: { xs: '100%', sm: 320 },
                    borderColor:
                      alignment.level === 'CONFLICTS'
                        ? '#dc2626'
                        : alignment.level === 'BOARD_ALIGNED'
                          ? '#059669'
                          : long
                            ? '#05966955'
                            : '#dc262655',
                    bgcolor: long ? '#05966908' : '#dc262608',
                    opacity: alignment.level === 'CONFLICTS' ? 0.72 : 1,
                    boxSizing: 'border-box',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.75} mb={0.5} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={s.side || '—'}
                      sx={{
                        fontWeight: 800,
                        bgcolor: long ? '#059669' : '#dc2626',
                        color: '#fff',
                      }}
                    />
                    <Typography fontWeight={800} fontSize={13}>
                      {s.symbol || s.kucoinTradeBase || s.instrumentId}
                    </Typography>
                    {s.confidence != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${(s.confidence * 100).toFixed(0)}%`}
                      />
                    )}
                    <Tooltip title={alignment.tip}>
                      <Chip
                        size="small"
                        color={alignment.chipColor}
                        variant={alignment.level === 'BOARD_ALIGNED' ? 'filled' : 'outlined'}
                        label={alignment.label}
                        sx={{
                          height: 'auto',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.35 },
                        }}
                      />
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {s.reason || s.name || ''}
                  </Typography>
                  {canOpenPositions && s.kucoinTradeable !== false ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => openSuggestionTrade(s)}
                    >
                      Open Trade
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {s.kucoinFuturesSymbol || 'KuCoin mapped'}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}
      {(board?.stockSuggestions || []).length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, p: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} mb={1}>
            <Typography fontWeight={800} fontSize={14}>
              Suggested stocks to open
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Research only — green = board agrees; amber = caution; red = skip. SHORT via KuCoin only
              (T212 is long-only).
            </Typography>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
            {board!.stockSuggestions!.map((s) => {
              const long = (s.side || '').toUpperCase() === 'LONG';
              const row = resolveStockSuggestionRow(s);
              const canKucoin = !!canOpenPositions && !!row?.kucoinTradeable;
              const canT212 = !!canTradeT212 && !!s.symbol;
              const alignment = getSuggestionAlignment(
                s.side,
                findBoardInstrument(board?.instruments, s.instrumentId, s.symbol),
                board?.macroRegime
              );
              return (
                <Paper
                  key={`stock-${s.instrumentId}-${s.side}`}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    minWidth: { xs: 0, sm: 200 },
                    flex: { xs: '1 1 100%', sm: '1 1 200px' },
                    maxWidth: { xs: '100%', sm: 320 },
                    borderColor:
                      alignment.level === 'CONFLICTS'
                        ? '#dc2626'
                        : alignment.level === 'BOARD_ALIGNED'
                          ? '#059669'
                          : long
                            ? '#05966955'
                            : '#dc262655',
                    bgcolor: long ? '#05966908' : '#dc262608',
                    opacity: alignment.level === 'CONFLICTS' ? 0.72 : 1,
                    boxSizing: 'border-box',
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.75} mb={0.5} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={s.side || '—'}
                      sx={{
                        fontWeight: 800,
                        bgcolor: long ? '#059669' : '#dc2626',
                        color: '#fff',
                      }}
                    />
                    <Typography fontWeight={800} fontSize={13}>
                      {s.symbol || s.instrumentId}
                    </Typography>
                    {s.confidence != null && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${(s.confidence * 100).toFixed(0)}%`}
                      />
                    )}
                    <Tooltip title={alignment.tip}>
                      <Chip
                        size="small"
                        color={alignment.chipColor}
                        variant={alignment.level === 'BOARD_ALIGNED' ? 'filled' : 'outlined'}
                        label={alignment.label}
                        sx={{
                          height: 'auto',
                          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.35 },
                        }}
                      />
                    </Tooltip>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {s.reason || s.name || ''}
                  </Typography>
                  <Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    {long ? (
                      <>
                        {canT212 && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => openStockSuggestionT212(s)}
                            sx={{ bgcolor: '#086afb', '&:hover': { bgcolor: '#0656c9' } }}
                          >
                            T212 Trade
                          </Button>
                        )}
                        {canKucoin && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => openStockSuggestionKuCoin(s)}
                          >
                            KuCoin Trade
                          </Button>
                        )}
                        {!canT212 && !canKucoin && (
                          <Typography variant="caption" color="text.disabled">
                            Trading access required
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        {canKucoin ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => openStockSuggestionKuCoin(s)}
                          >
                            KuCoin Trade
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            {row?.kucoinFuturesSymbol
                              ? 'KuCoin trade permission required'
                              : 'No KuCoin perp mapped'}
                          </Typography>
                        )}
                        {canT212 && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openStockSuggestionT212(s)}
                            sx={{ borderColor: '#086afb', color: '#086afb' }}
                          >
                            View on T212
                          </Button>
                        )}
                      </>
                    )}
                  </Stack>
                  {!long && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                      SHORT opens on KuCoin. T212 cannot short — View only sells shares you already hold.
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      )}
      {board?.disclaimer && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {board.disclaimer}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab label="Price board" />
        <Tab label="Charts" />
        <Tab label="News & triggers" />
        <Tab label="Correlations" />
        <Tab label="Playbooks" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Stack direction="row" gap={1} mb={2} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, maxWidth: '100%' }}>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="ALL">All</MenuItem>
                {CATEGORY_ORDER.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {CATEGORY_ORDER.map((c) => (
              <Chip
                key={c}
                label={`${c}: ${(board?.byCategory?.[c] || []).length}`}
                size="small"
                variant={categoryFilter === c ? 'filled' : 'outlined'}
                onClick={() => setCategoryFilter(c)}
              />
            ))}
          </Stack>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxWidth: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Change</TableCell>
                  <TableCell align="right">Change %</TableCell>
                  <TableCell>Bias</TableCell>
                  <TableCell>Trade</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {instruments.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={row.id === selectedId}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedId(row.id);
                      setTab(1);
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight={700} fontSize={13}>
                        {row.symbol}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.category} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {fmtPrice(row.price, row.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: pctColor(row.changePercent), fontFamily: 'monospace' }}>
                      {row.change == null ? '—' : row.change.toFixed(4)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: pctColor(row.changePercent), fontWeight: 700, fontFamily: 'monospace' }}>
                      {row.changePercent == null ? '—' : `${row.changePercent >= 0 ? '+' : ''}${row.changePercent.toFixed(2)}%`}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.bias || 'FLAT'}
                        color={row.bias === 'BULLISH' ? 'success' : row.bias === 'BEARISH' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <KucoinTradeChips
                        instrument={row}
                        dense
                        canTrade={canOpenPositions}
                        onTrade={openTradeDrawer}
                        canTradeT212={canTradeT212}
                        onTradeT212={openTrading212}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{row.source}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.note}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Use <strong>Trade</strong> for KuCoin futures (same drawer as Market Signals). On STOCK rows,
            use <strong>T212 Trade</strong> to open the real equity on your Trading 212 account via Stock
            Portfolio. External KuCoin chips open the exchange website. Crypto-venue products (funding /
            leverage) are not the same as Yahoo/CNBC exchange futures.
            {!canOpenPositions && (
              <> Requires <code>OPEN_MARKET_SIGNAL_POSITIONS</code> or <code>MANAGE_TRADING_BOTS</code> for KuCoin trade.</>
            )}
            {!canTradeT212 && (
              <> T212 Trade requires <code>VIEW_STOCK_PORTFOLIO</code>.</>
            )}
          </Typography>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} mb={2} alignItems={{ md: 'center' }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, maxWidth: '100%' }}>
              <InputLabel>Instrument</InputLabel>
              <Select
                label="Instrument"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {(board?.instruments || []).map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.symbol} — {i.name}
                    {i.kucoinTradeable ? ' · KuCoin' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 }, maxWidth: '100%' }}>
              <InputLabel>Range</InputLabel>
              <Select label="Range" value={range} onChange={(e) => setRange(e.target.value)}>
                <MenuItem value="1d">1 day</MenuItem>
                <MenuItem value="5d">5 days</MenuItem>
                <MenuItem value="1mo">1 month</MenuItem>
                <MenuItem value="3mo">3 months</MenuItem>
                <MenuItem value="6mo">6 months</MenuItem>
                <MenuItem value="1y">1 year</MenuItem>
              </Select>
            </FormControl>
            {selected && (
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Chip label={selected.category} size="small" />
                <Typography fontWeight={800}>{fmtPrice(selected.price, selected.currency)}</Typography>
                <Typography sx={{ color: pctColor(selected.changePercent), fontWeight: 700 }}>
                  {selected.changePercent == null
                    ? ''
                    : `${selected.changePercent >= 0 ? '+' : ''}${selected.changePercent.toFixed(2)}%`}
                </Typography>
                <KucoinTradeChips
                  instrument={selected}
                  canTrade={canOpenPositions}
                  onTrade={openTradeDrawer}
                  canTradeT212={canTradeT212}
                  onTradeT212={openTrading212}
                />
              </Stack>
            )}
          </Stack>

          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, height: { xs: 280, md: 420 } }}>
            {historySource && historyPoints.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Chart source: {historySource}
                {historySource.includes('etf-proxy')
                  ? ' — ETF proxy used because the futures feed was unavailable (direction/trend is useful; absolute price may differ from the futures contract).'
                  : historySource.startsWith('cnbc')
                    ? ' — CNBC quote fallback used because Yahoo was unavailable on the server.'
                    : ''}
              </Typography>
            )}
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : historyPoints.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" height="100%" gap={1}>
                <Typography color="text.secondary">
                  No chart data for this instrument/range yet.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Feeds may be blocked on the server — click Refresh after redeploying the latest backend.
                </Typography>
                <Button size="small" variant="outlined" onClick={() => loadHistory(selectedId, range)}>
                  Retry chart
                </Button>
              </Stack>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyPoints}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" hide />
                  <YAxis domain={['auto', 'auto']} width={70} tick={{ fontSize: 11 }} />
                  <RechartsTooltip
                    formatter={(value: any) => [fmtPrice(Number(value)), selected?.symbol || 'Price']}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ''}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="price"
                    name={selected?.symbol || 'Price'}
                    stroke="#2563eb"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Box>
      )}

      {tab === 2 && (
        <Stack gap={2}>
          <Alert severity="warning">
            Headlines are matched to scenario playbooks (e.g. ceasefire collapse → oil ↑, risk assets ↓).
            This is expected-direction research, not proven causation.
          </Alert>
          {(board?.triggerEvents || []).map((ev) => (
            <Paper key={ev.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap" mb={1}>
                <Typography fontWeight={800}>{ev.headline}</Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                  {ev.liveHeadline && <Chip size="small" color="info" label="Live headline" />}
                  {!ev.liveHeadline && <Chip size="small" label="Playbook / alert" />}
                  {ev.scenario && <Chip size="small" variant="outlined" label={ev.scenario} />}
                  {ev.confidence != null && (
                    <Chip size="small" label={`Conf ${(ev.confidence * 100).toFixed(0)}%`} />
                  )}
                </Stack>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {ev.summary}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                {ev.source}
                {ev.publishedEpochSec
                  ? ` · ${new Date(ev.publishedEpochSec * 1000).toLocaleString()}`
                  : ''}
                {ev.url ? (
                  <>
                    {' · '}
                    <a href={ev.url} target="_blank" rel="noreferrer">
                      Open
                    </a>
                  </>
                ) : null}
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {(ev.expectedImpacts || []).map((imp, idx) => (
                  <Tooltip key={`${ev.id}-${idx}`} title={imp.reason || ''}>
                    <Chip
                      size="small"
                      label={`${imp.asset} ${imp.direction}`}
                      color={
                        imp.direction === 'UP'
                          ? 'success'
                          : imp.direction === 'DOWN'
                            ? 'error'
                            : 'default'
                      }
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {tab === 3 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Pairwise Pearson correlation of recent daily returns (approx. 1 month). Includes every
            instrument with enough history (commodities, FX, crypto, equities). Values near +1 move
            together; near −1 move inversely. Use with news triggers to decide hedges or pairs.
            {corrSymbols.length > 0 && (
              <> Currently showing <strong>{corrSymbols.length}</strong> instruments.</>
            )}
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Symbol</TableCell>
                  {corrSymbols.map((s) => (
                    <TableCell key={s} align="center" sx={{ fontWeight: 700, fontSize: 11 }}>
                      {s}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(board?.correlationMatrix || []).map((row) => (
                  <TableRow key={row.symbol}>
                    <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                    {corrSymbols.map((s) => {
                      const v = row.values?.[s] ?? 0;
                      return (
                        <TableCell
                          key={`${row.symbol}-${s}`}
                          align="center"
                          sx={{ bgcolor: heatColor(v), fontFamily: 'monospace', fontSize: 12 }}
                        >
                          {v.toFixed(2)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === 4 && (
        <Stack gap={2}>
          {(board?.playbooks || []).map((pb) => (
            <Paper key={pb.id} variant="outlined" sx={{ p: 2 }}>
              <Chip size="small" label={pb.id} sx={{ mb: 1 }} />
              <Typography fontWeight={800} mb={0.5}>
                Trigger: {pb.trigger}
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Typical move:</strong> {pb.typicalMove}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Trading note:</strong> {pb.tradingNote}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      <CoinPositionsDrawer
        coin={drawerCoin}
        open={drawerOpen}
        onClose={handleCloseTradeDrawer}
        isAdminContext
        canOpenPositions={canOpenPositions}
        onViewStrategy={handleViewStrategyFromDrawer}
      />
      <ActivityBotCreateDrawer
        open={activityBotDrawerOpen}
        onClose={() => setActivityBotDrawerOpen(false)}
        lockedSectorId="COMMODITIES"
        title="New commodities activity bot"
        subtitle="Same create flow as Crypto Portfolio — sector locked to Commodities"
        onSaved={() =>
          setActivityBotSavedMsg('Commodities activity bot saved. Start or manage it from Crypto Portfolio.')
        }
      />
    </Box>
  );
};

export default AdminMarkets;
